import { useState } from "react";
import { Loader2, Coins, AlertTriangle } from "lucide-react";
import { useCosts } from "@/context/CostsContext";

/**
 * A single-purpose button for any action that spends credits.
 *
 * Always renders an inline "· N credits" label next to the button label so
 * users never click blind. If the action cost is >= confirm_modal_threshold
 * (from /api/costs, currently 5), clicking the button opens a confirm modal
 * showing the exact cost before firing the action.
 *
 * Iter34 · every audit / DFS action / AI action / keyword add uses this so
 * nothing spends credits without an explicit tap-through.
 *
 * Props:
 *   cost           number — explicit credits cost (overrides actionKey lookup)
 *   actionKey      string — key in CREDIT_COSTS (audit, brief, backlinks_refresh, ...)
 *   costMultiplier number — for per-N actions (e.g. keyword_add × N)
 *   onConfirm      async fn — the actual action; called only after user confirms
 *   children       string — button label (e.g. "Run audit")
 *   description    string — extra sentence shown inside the modal
 *   disabled, className, testid, variant
 */
export function CreditActionButton({
    cost,
    actionKey,
    costMultiplier = 1,
    onConfirm,
    children,
    description = "",
    disabled = false,
    className = "",
    testid = "credit-action-btn",
    variant = "primary", // "primary" | "outline"
}) {
    const { costs, confirm_modal_threshold } = useCosts();
    const resolvedCost = Math.max(
        0,
        Math.round((cost ?? costs?.[actionKey] ?? 0) * (costMultiplier || 1)),
    );
    const [busy, setBusy] = useState(false);
    const [modalOpen, setModalOpen] = useState(false);

    const run = async () => {
        setBusy(true);
        try {
            await onConfirm();
        } finally {
            setBusy(false);
            setModalOpen(false);
        }
    };

    const handleClick = (e) => {
        e.preventDefault?.();
        if (disabled || busy) return;
        if (resolvedCost >= (confirm_modal_threshold ?? 5)) {
            setModalOpen(true);
            return;
        }
        run();
    };

    const base =
        variant === "outline"
            ? "border border-border bg-transparent text-foreground hover:bg-card"
            : "bg-primary text-primary-foreground hover:bg-primary/90";

    return (
        <>
            <button
                data-testid={testid}
                data-cost={resolvedCost}
                onClick={handleClick}
                disabled={disabled || busy}
                className={`${base} px-4 py-2 text-sm font-medium inline-flex items-center gap-2 disabled:opacity-50 ${className}`}
            >
                {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : null}
                <span>{children}</span>
                <span
                    data-testid={`${testid}-cost`}
                    className="text-[10px] font-mono opacity-80 inline-flex items-center gap-1 border-l border-current/30 pl-2 ml-1"
                >
                    <Coins className="w-3 h-3" />
                    {resolvedCost === 0
                        ? "free"
                        : `${resolvedCost} credit${resolvedCost === 1 ? "" : "s"}`}
                </span>
            </button>

            {modalOpen && (
                <div
                    data-testid={`${testid}-modal`}
                    className="fixed inset-0 z-50 bg-black/70 backdrop-blur-sm flex items-center justify-center px-4"
                    onClick={() => !busy && setModalOpen(false)}
                >
                    <div
                        className="bg-card border border-border max-w-md w-full p-6"
                        onClick={(e) => e.stopPropagation()}
                    >
                        <div className="overline mb-3 flex items-center gap-2">
                            <AlertTriangle className="w-3 h-3 text-primary" />
                            CONFIRM · SPENDS CREDITS
                        </div>
                        <h3 className="font-display text-2xl tracking-tight mb-3">
                            {children}?
                        </h3>
                        <p className="text-sm text-muted-foreground mb-4">
                            This action will spend{" "}
                            <span className="font-mono text-foreground font-bold">
                                {resolvedCost} credit{resolvedCost === 1 ? "" : "s"}
                            </span>{" "}
                            from your balance.
                        </p>
                        {description && (
                            <p className="text-xs text-muted-foreground border-l border-border pl-3 mb-6">
                                {description}
                            </p>
                        )}

                        <div className="flex items-center justify-end gap-2 mt-6">
                            <button
                                data-testid={`${testid}-cancel`}
                                onClick={() => setModalOpen(false)}
                                disabled={busy}
                                className="border border-border bg-transparent px-4 py-2 text-sm font-medium hover:bg-card disabled:opacity-50"
                            >
                                Cancel
                            </button>
                            <button
                                data-testid={`${testid}-confirm`}
                                onClick={run}
                                disabled={busy}
                                className="bg-primary text-primary-foreground px-4 py-2 text-sm font-medium hover:bg-primary/90 inline-flex items-center gap-2 disabled:opacity-50"
                            >
                                {busy ? (
                                    <><Loader2 className="w-4 h-4 animate-spin" /> Running…</>
                                ) : (
                                    <>Confirm & spend {resolvedCost}</>
                                )}
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </>
    );
}

/**
 * Inline cost badge for surfaces that don't use CreditActionButton (e.g.
 * disabled state, informational rows). Renders "· N credits" style label.
 */
export function CostBadge({ cost, actionKey, className = "", testid }) {
    const { costs } = useCosts();
    const resolved = cost ?? costs?.[actionKey] ?? 0;
    return (
        <span
            data-testid={testid || "cost-badge"}
            data-cost={resolved}
            className={`text-[10px] font-mono text-muted-foreground inline-flex items-center gap-1 ${className}`}
        >
            <Coins className="w-3 h-3" />
            {resolved === 0 ? "free" : `${resolved} credit${resolved === 1 ? "" : "s"}`}
        </span>
    );
}
